import { QueryFunction } from 'react-query';
import queryClient from '@/lib/query-client';
import { getOrgBranding } from './services/org.service';
import { getUserPolicy } from './services/policy.service';
import { getSettings as getFileSettings } from './services/file.service';
import { getPublicServiceAppData } from './services/public-service.service';

export type AppDataQueryKey =
  | 'org-branding'
  | 'portal-policy'
  | 'file-settings'
  | 'public-service-catalog';

const HOUR = 1000 * 60 * 60;
const MINUTE = 1000 * 60;

export type QueryOptions = {
  cacheTime?: number; // how long before garbage collection
  staleTime?: number; // how long before refetch
  enabled?: boolean;
};

export const AppDataQueries: Map<
  AppDataQueryKey,
  { queryFn: QueryFunction; options: QueryOptions }
> = new Map();

// Organization branding
AppDataQueries.set('org-branding', {
  queryFn: getOrgBranding,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
});

// Portal policy
AppDataQueries.set('portal-policy', {
  queryFn: getUserPolicy,
  options: {
    staleTime: MINUTE,
    cacheTime: MINUTE,
  },
});

// File handler settings
AppDataQueries.set('file-settings', {
  queryFn: getFileSettings,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
});

// Public Services Catalog
AppDataQueries.set('public-service-catalog', {
  queryFn: getPublicServiceAppData,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
});

export const prefetchAppData = async () => {
  const prefetchQueries: Promise<void>[] = [];

  for (const [queryKey, { queryFn, options }] of AppDataQueries) {
    prefetchQueries.push(queryClient.prefetchQuery(queryKey, queryFn, options));
  }

  return Promise.all(prefetchQueries);
};

export const fetchAppData = async <T>(queryKey: AppDataQueryKey) => {
  const appDataQuery = AppDataQueries.get(queryKey)!;
  return queryClient.fetchQuery<T>(
    queryKey,
    appDataQuery.queryFn as () => Promise<T>,
    appDataQuery.options
  );
};
